<template>
  <div class="mc-portal">
    <PortalTopBar role="staff" />

    <div class="pt-container">
      <!-- Greeting + quick actions -->
      <div class="pt-head">
        <div>
          <div class="pt-kicker">{{ todayLabel }}</div>
          <h1 class="pt-title">Welcome back, {{ firstName }}</h1>
          <p class="pt-sub">Here's what needs you today across your cities and towns.</p>
        </div>
        <div class="quick">
          <button type="button" class="pt-btn pt-btn-primary" @click="go('/staff-leads')">
            <SiteIcon name="plus" :size="17" /> Add a lead
          </button>
          <button type="button" class="pt-btn pt-btn-outline" @click="go('/staff-onboard-customer')">
            <SiteIcon name="userPlus" :size="17" /> Onboard a city
          </button>
          <button type="button" class="pt-btn pt-btn-outline" @click="go('/staff/templates')">
            <SiteIcon name="mail" :size="17" /> Templates
          </button>
          <button type="button" class="pt-btn pt-btn-outline" @click="go('/staff/blog')">
            <SiteIcon name="news" :size="17" /> Write a post
          </button>
        </div>
      </div>

      <!-- KPIs -->
      <div class="kpis">
        <div v-for="k in kpis" :key="k.label" class="kpi" :class="{ clickable: k.to }" @click="k.to && go(k.to)">
          <div class="kpi-top">
            <span class="kpi-label">{{ k.label }}</span>
            <SiteIcon :name="k.icon" :size="18" />
          </div>
          <div class="kpi-value">{{ k.value === null ? "—" : k.value.toLocaleString() }}</div>
          <div class="kpi-meta">{{ k.meta }}</div>
        </div>
      </div>

      <!-- Pipeline, last 7 days -->
      <section class="pt-card">
        <div class="pt-card-head">
          <h2 class="pt-card-title"><SiteIcon name="chart" :size="20" /> The last 7 days</h2>
          <span class="pt-hint">Whole team</span>
        </div>
        <div class="charts">
          <div v-for="c in charts" :key="c.label" class="chart">
            <div class="chart-head">
              <span class="chart-label">{{ c.label }}</span>
              <b class="chart-total">{{ sum(c.data) }}</b>
            </div>
            <div class="bars">
              <div v-for="(v, i) in c.data" :key="i" class="bar-col" :title="`${dayNames[i]}: ${v}`">
                <span class="bar" :class="c.tone" :style="{ height: barHeight(v, c.data) }"></span>
                <span class="bar-day">{{ dayNames[i] }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div class="two-col">
        <!-- Today's follow-ups (my leads with a next step due) -->
        <section class="pt-card">
          <div class="pt-card-head">
            <h2 class="pt-card-title">
              <SiteIcon name="calendarCheck" :size="20" /> Your follow-ups
              <span class="pt-count">{{ followUps.length }}</span>
            </h2>
            <button type="button" class="pt-btn pt-btn-ghost pt-btn-sm" @click="go('/staff-leads')">Open pipeline</button>
          </div>

          <div v-if="loadingFollowUps">
            <div v-for="n in 3" :key="n" class="pt-skeleton"></div>
          </div>
          <div v-else-if="!followUps.length" class="pt-empty small">
            <SiteIcon name="check" :size="34" />
            <h3>Nothing due</h3>
            <p>No calls, emails or demos are due today. Set a next step on a lead to see it here.</p>
          </div>
          <ul v-else class="follow">
            <li v-for="f in followUps" :key="f.id" class="follow-item" @click="go('/staff-leads')">
              <span class="follow-type">{{ actionLabel(f.nextAction.type) }}</span>
              <div class="follow-text">
                <div class="follow-city">{{ f.companyName }}</div>
                <div class="follow-note">{{ f.nextAction.note || f.contactName || "No note" }}</div>
              </div>
              <span class="pt-badge" :class="f.overdue ? 'red' : 'amber'">{{ f.overdue ? "Overdue" : "Today " + time(f.nextAction.dueAt) }}</span>
            </li>
          </ul>
        </section>

        <!-- Leaderboard -->
        <section class="pt-card">
          <div class="pt-card-head">
            <h2 class="pt-card-title"><SiteIcon name="trophy" :size="20" /> Team leaderboard</h2>
            <span class="pt-hint">Points from leads, calls, demos and wins</span>
          </div>

          <div v-if="leaderboardLoading">
            <div v-for="n in 3" :key="n" class="pt-skeleton"></div>
          </div>
          <div v-else-if="!leaderboard.length" class="pt-empty small">
            <SiteIcon name="trophy" :size="34" />
            <h3>No points yet</h3>
            <p>Points appear as the team adds leads and logs calls.</p>
          </div>
          <ol v-else class="board">
            <li v-for="(m, i) in leaderboard" :key="m.id" class="board-item" :class="{ me: m.id === uid }">
              <span class="rank" :class="'r' + Math.min(i + 1, 4)">{{ i + 1 }}</span>
              <span class="board-avatar">{{ m.initials }}</span>
              <div class="board-text">
                <div class="board-name">{{ m.name }}<span v-if="m.id === uid" class="you">You</span></div>
                <div class="board-meta">{{ m.deals }} {{ m.deals === 1 ? "city won" : "cities won" }}</div>
              </div>
              <b class="board-points">{{ m.points.toLocaleString() }}</b>
            </li>
          </ol>
        </section>
      </div>

      <footer class="pt-foot">© {{ year }} Municipal Collectionz · Staff portal</footer>
    </div>
  </div>
</template>

<script setup>
import "@/styles/portal.css";
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { auth, db } from "@/firebase";
import router from "@/router";
import {
  collection, onSnapshot, query, where, orderBy, limit, doc, getDoc,
  getCountFromServer, collectionGroup,
} from "firebase/firestore";
import PortalTopBar from "@/components/PortalTopBar.vue";
import SiteIcon from "@/components/SiteIcon.vue";

const year = new Date().getFullYear();
const todayLabel = new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
const uid = ref(null);
const firstName = ref("");

/* ---------- KPIs (all live) ---------- */
const activeCustomers = ref(null);
const openLeads = ref(null);
const wonThisMonth = ref(null);
const newRequests = ref(null);

const kpis = computed(() => [
  { label: "Cities live", value: activeCustomers.value, meta: "Customers on the platform", icon: "city" },
  { label: "Open leads", value: openLeads.value, meta: "New through proposal", icon: "target", to: "/staff-leads" },
  { label: "Won this month", value: wonThisMonth.value, meta: "Contracts closed", icon: "trophy" },
  { label: "Demo requests", value: newRequests.value, meta: "From the website, waiting", icon: "calendarCheck", to: "/staff-leads" },
]);

/* ---------- Last 7 days ---------- */
function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function windowStart() {
  const t = new Date();
  t.setDate(t.getDate() - 6);
  return startOfDay(t);
}
function toDate(ts) {
  if (!ts) return null;
  if (ts.seconds) return new Date(ts.seconds * 1000);
  if (ts.toDate) return ts.toDate();
  return new Date(ts);
}
function dayIdx(ts) {
  const d = toDate(ts);
  if (!d) return -1;
  const i = Math.floor((startOfDay(d).getTime() - windowStart().getTime()) / 86400000);
  return i >= 0 && i < 7 ? i : -1;
}
const empty7 = () => [0, 0, 0, 0, 0, 0, 0];
const dayNames = Array.from({ length: 7 }, (_, i) => {
  const d = windowStart();
  d.setDate(d.getDate() + i);
  return d.toLocaleDateString(undefined, { weekday: "narrow" });
});

const sparkInbound = ref(empty7());
const sparkProgress = ref(empty7());
const sparkProposals = ref(empty7());
const charts = computed(() => [
  { label: "New leads", data: sparkInbound.value, tone: "ink" },
  { label: "Calls, emails & meetings", data: sparkProgress.value, tone: "green" },
  { label: "Demos & proposals scheduled", data: sparkProposals.value, tone: "brass" },
]);
const sum = (arr) => arr.reduce((a, b) => a + b, 0);
const barHeight = (v, arr) => {
  const max = Math.max(...arr, 1);
  return `${Math.max((v / max) * 100, v ? 8 : 3)}%`;
};

/* ---------- Follow-ups ---------- */
const myLeads = ref([]);
const loadingFollowUps = ref(true);
const OPEN = ["new", "contacted", "qualified", "proposal"];
const followUps = computed(() => {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const now = Date.now();
  return myLeads.value
    .filter((l) => OPEN.includes(l.status) && l.nextAction && l.nextAction.dueAt)
    .map((l) => ({ ...l, due: toDate(l.nextAction.dueAt) }))
    .filter((l) => l.due && l.due <= end)
    .map((l) => ({ ...l, overdue: l.due.getTime() < now }))
    .sort((a, b) => a.due - b.due);
});
const ACTIONS = { call: "Call", email: "Email", meeting: "Meeting", demo: "Demo", proposal: "Proposal" };
const actionLabel = (t) => ACTIONS[t] || t;
const time = (ts) => toDate(ts)?.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) || "";

/* ---------- Leaderboard ---------- */
const leaderboard = ref([]);
const leaderboardLoading = ref(true);
const initialsFrom = (name) => (name || "?").split(/\s+/).map((p) => p[0]).join("").slice(0, 2).toUpperCase();

async function hydrateUserRow(id, points) {
  let u = {};
  try {
    const snap = await getDoc(doc(db, "users", id));
    u = snap.exists() ? snap.data() : {};
  } catch (e) {
    u = {};
  }
  const name = `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.fullName || u.email || "Team member";
  let deals = 0;
  try {
    const won = await getCountFromServer(query(collection(db, "leads"), where("ownerUid", "==", id), where("status", "==", "won")));
    deals = won.data().count || 0;
  } catch (e) {
    deals = 0;
  }
  return { id, name, initials: initialsFrom(name), points: Number(points) || 0, deals };
}

/* ---------- Subscriptions ---------- */
const unsubs = [];
function listen(q, fn, onErr) {
  try {
    unsubs.push(onSnapshot(q, fn, (err) => {
      console.error("Dashboard query error:", err);
      if (onErr) onErr(err);
    }));
  } catch (e) {
    console.error(e);
  }
}

function start(user) {
  uid.value = user.uid;
  getDoc(doc(db, "users", user.uid))
    .then((s) => {
      const d = s.exists() ? s.data() : {};
      firstName.value = d.firstName || (d.fullName || user.displayName || user.email || "there").split(/[\s@]/)[0];
    })
    .catch(() => (firstName.value = "there"));

  listen(collection(db, "customers"), (s) => (activeCustomers.value = s.size), () => (activeCustomers.value = 0));
  listen(query(collection(db, "leads"), where("status", "in", OPEN)), (s) => (openLeads.value = s.size), () => (openLeads.value = 0));
  listen(query(collection(db, "leads"), where("status", "==", "won")), (s) => {
    const monthStart = new Date();
    monthStart.setDate(1);
    monthStart.setHours(0, 0, 0, 0);
    wonThisMonth.value = s.docs.filter((d) => (toDate(d.data().closedAt) || 0) >= monthStart).length;
  }, () => (wonThisMonth.value = 0));
  listen(query(collection(db, "contact_requests"), where("status", "==", "new")), (s) => (newRequests.value = s.size), () => (newRequests.value = 0));

  listen(query(collection(db, "leads"), where("createdAt", ">=", windowStart())), (s) => {
    const b = empty7();
    s.forEach((d) => {
      const i = dayIdx(d.data().createdAt);
      if (i >= 0) b[i]++;
    });
    sparkInbound.value = b;
  });
  listen(query(collectionGroup(db, "interactions"), where("at", ">=", windowStart())), (s) => {
    const b = empty7();
    s.forEach((d) => {
      const i = dayIdx(d.data().at);
      if (i >= 0) b[i]++;
    });
    sparkProgress.value = b;
  });
  listen(query(collection(db, "leads"), where("nextAction.dueAt", ">=", windowStart())), (s) => {
    const b = empty7();
    s.forEach((d) => {
      const na = d.data().nextAction;
      if (!na || !["demo", "proposal"].includes((na.type || "").toLowerCase())) return;
      const i = dayIdx(na.dueAt);
      if (i >= 0) b[i]++;
    });
    sparkProposals.value = b;
  });

  listen(query(collection(db, "leads"), where("ownerUid", "==", user.uid)), (s) => {
    myLeads.value = s.docs.map((d) => ({ id: d.id, ...d.data() }));
    loadingFollowUps.value = false;
  }, () => (loadingFollowUps.value = false));

  listen(query(collection(db, "user_stats"), orderBy("points", "desc"), limit(10)), async (s) => {
    leaderboardLoading.value = true;
    leaderboard.value = (await Promise.all(s.docs.map((d) => hydrateUserRow(d.id, d.data().points)))).filter((r) => r.points > 0);
    leaderboardLoading.value = false;
  }, () => (leaderboardLoading.value = false));
}

let unsubAuth;
onMounted(() => {
  unsubAuth = auth.onAuthStateChanged((u) => {
    if (u && !uid.value) start(u);
  });
});
onBeforeUnmount(() => {
  if (unsubAuth) unsubAuth();
  unsubs.forEach((u) => u && u());
});

const go = (path) => router.push(path);
</script>

<style scoped>
.quick {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 22px;
}

.kpi {
  padding: 18px 20px;
  border: 1px solid var(--rule);
  border-top: 3px solid var(--ink);
  border-radius: 4px 4px 10px 10px;
  background: var(--card);
}

.kpi:nth-child(2) {
  border-top-color: var(--green);
}

.kpi:nth-child(3) {
  border-top-color: var(--brass);
}

.kpi:nth-child(4) {
  border-top-color: #7cc4a5;
}

.kpi.clickable {
  cursor: pointer;
}

.kpi.clickable:hover {
  box-shadow: 0 10px 24px rgba(14, 34, 54, 0.08);
}

.kpi-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--muted);
}

.kpi-label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.kpi-value {
  margin: 8px 0 2px;
  font-family: var(--serif);
  font-size: 2.6rem;
  font-weight: 700;
  line-height: 1;
}

.kpi-meta {
  font-size: 13px;
  color: var(--muted);
}

.charts {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 22px;
}

.chart {
  padding: 16px;
  border: 1px solid var(--rule);
  border-radius: 8px;
  background: #fff;
}

.chart-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 12px;
}

.chart-label {
  font-size: 13px;
  font-weight: 700;
  color: var(--ink-2);
}

.chart-total {
  font-family: var(--serif);
  font-size: 1.5rem;
}

.bars {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
  height: 120px;
}

.bar-col {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
}

.bar {
  width: 100%;
  max-width: 26px;
  border-radius: 4px 4px 0 0;
  transition: height 0.4s ease;
}

.bar.ink {
  background: var(--ink);
}

.bar.green {
  background: var(--green);
}

.bar.brass {
  background: var(--brass);
}

.bar-day {
  font-size: 11px;
  color: var(--muted);
}

.two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 22px;
  margin-top: 22px;
}

.two-col .pt-card + .pt-card {
  margin-top: 0;
}

.two-col > *,
.kpis > *,
.charts > * {
  min-width: 0;
}

.pt-empty.small {
  padding: 30px 16px;
}

.follow,
.board {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 8px;
}

.follow-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid var(--rule);
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
}

.follow-item:hover {
  border-color: var(--green);
}

.follow-type {
  width: 74px;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--green);
}

.follow-text {
  flex: 1;
  min-width: 0;
}

.follow-city {
  font-weight: 700;
}

.follow-note {
  font-size: 13px;
  color: var(--muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.board-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border: 1px solid var(--rule);
  border-radius: 8px;
  background: #fff;
}

.board-item.me {
  border-color: var(--green);
  background: var(--green-tint);
}

.rank {
  width: 24px;
  font-family: var(--serif);
  font-weight: 700;
  color: var(--muted);
}

.rank.r1 {
  color: var(--brass);
}

.board-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--ink);
  color: var(--on-navy);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--serif);
  font-size: 0.85rem;
  font-weight: 700;
  flex-shrink: 0;
}

.board-text {
  flex: 1;
  min-width: 0;
}

.board-name {
  font-weight: 700;
}

.you {
  margin-left: 8px;
  padding: 1px 7px;
  border-radius: 999px;
  background: var(--green);
  color: #fff;
  font-size: 10.5px;
  font-weight: 700;
}

.board-meta {
  font-size: 12.5px;
  color: var(--muted);
}

.board-points {
  font-family: var(--serif);
  font-size: 1.2rem;
}

.pt-foot {
  margin-top: 40px;
  text-align: center;
  font-size: 13px;
  color: var(--muted);
}

@media (max-width: 1100px) {
  .kpis {
    grid-template-columns: repeat(2, 1fr);
  }
  .charts {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 860px) {
  .two-col {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 520px) {
  .follow-type {
    width: auto;
  }
  .follow-item {
    flex-wrap: wrap;
  }
  .kpis {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
  .kpi-value {
    font-size: 2rem;
  }
  .quick .pt-btn {
    flex: 1 1 calc(50% - 8px);
  }
}
</style>